import { useState, useCallback } from 'react';

/**
 * A typed hook similar to useState, but specialized for object state.
 * Allows updating a single property without replacing the whole object.
 *
 * @example
 * const [state, setState] = useObjectState({ name: 'John', age: 30 });
 * setState({ age: 31 }); // updates only the age property
 * setState(prev => ({ name: prev.name.toUpperCase() })); // updates name based on previous state
 *
 */
export function useObjectState<T extends object>(
	initialState: T
): [T, (update: Partial<T> | ((prev: T) => Partial<T>)) => void] {
	const [state, setState] = useState<T>(initialState);

	const updateState = useCallback(
		(update: Partial<T> | ((prev: T) => Partial<T>)) => {
			setState((prev) => {
				const patch = typeof update === 'function' ? update(prev) : update;
				return { ...prev, ...patch };
			});
		},
		[]
	);

	return [state, updateState];
}
